import redisClient from './redisClient';
import redisConfig from './config/redis.config';
import logRedisError from './utils/logRedisError';

import isEmpty from 'lodash/isEmpty';

import getHealthCheckStatus from '../health-check/utils/getHealthCheckStatus';

function checkObject(data: Record<string, any>, allowedToAccessRedis: boolean[], nestedObjectCheck?: boolean) {
  for (const [key] of Object.entries(data)) {
    if (!isEmpty(data[key])) {
      const isObject = data[key] !== null && typeof data[key] === 'object';
      // Allow maximum of one recursion
      if (isObject && !nestedObjectCheck) {
        checkObject(data[key], allowedToAccessRedis, true);
      } else {
        allowedToAccessRedis.push(true);
      }
    } else {
      allowedToAccessRedis.push(false);
    }
  }
}
export default class RedisCache {
  public cacheOptions = { key: '', ttl: 0 };
  public isEnabled = false;

  /**
   *  Check if data is valid before data is allowed to store
   */
  public allowToSetToCache(response: any) {
    const isArray = response !== null && Array.isArray(response);
    const isObject = response !== null && typeof response === 'object';
    const allowedToAccessRedis: boolean[] = [];

    // Allow to set data if data is not an object|array and is valid
    if (!(isArray || isObject)) {
      allowedToAccessRedis.push(!isEmpty(response));
    }

    // Allow to set data if data array and valid
    if (isArray) {
      allowedToAccessRedis.push(!isEmpty(response));
    }

    // Allow to set data if data object and valid
    if (isObject) {
      const isObjectValid = !isEmpty(response);

      if (isObjectValid) {
        // Check nested nested object data
        checkObject(response, allowedToAccessRedis);
      } else {
        allowedToAccessRedis.push(false);
      }
    }

    // If array has a negative boolean then allowing to set to redis is denied
    return allowedToAccessRedis.every(Boolean);
  }

  /**
   *  Set request variables for redis
   *  @param key - key must be unique, redis uses this key to set/get
   *  @param ttl - ttl must be bigger than 0, ttl shows how long redis stores data for this request
   */
  public setCacheOptions({ key, ttl }: { key: string; ttl: number }) {
    this.cacheOptions = { key, ttl };
    return this;
  }

  /**
   *  Enable redis - this must only be called after redis request options have been set
   *  @param enable - enable/disable redis. By default redis is disabled for the request
   */
  public enable(enable: boolean) {
    const { key, ttl } = this.cacheOptions;

    if (process.env.NODE_ENV === 'development' && (!key || !(ttl > 0))) {
      logRedisError(key, ttl);
      return;
    }

    // Allow service to enable redis only when redis is enabled from configuration and when redis is healthy
    const isRedisHealthy = getHealthCheckStatus().redis;
    this.isEnabled = redisConfig.enableRedis && isRedisHealthy && enable;
  }

  /**
   *  Get data from redis
   *  @param key - key must be unique, redis uses this key to set/get
   */
  public async getFromCache(key: string) {
    const response = await redisClient.getFromRedis(key);
    return response;
  }

  /**
   * Set data to redis
   * @param key - key must be unique, redis uses this key to set/get
   * @param data - redis saves this data
   * @param expires - redis stores data for x amount of time
   */
  public async setToCache(key: string, data: any, expires: number) {
    await redisClient.setToRedis(key, data, expires);
  }
}
